import type { ElementType, Ref, InputHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { colors, fontSizes } from '@anyroad/theme';
import styled from '@emotion/styled';
import NumberFormat from 'react-number-format';

export type PriceInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value'
> & {
  allowNegative?: boolean;
  as?: ElementType<unknown>;
  currencySymbol?: string;
  dataTestId?: string;
  decimalScale?: number;
  decimalSeparator?: string;
  defaultValue?: number | string;
  fixedDecimalScale?: boolean;
  intlConfig?: {
    currency: string;
    locale: string;
  };
  label?: string;
  onValueChange: (value?: string) => void;
  prefix?: string;
  ref?: Ref<NumberFormat>;
  suffix?: string;
  thousandSeparator?: string;
  type?: 'tel' | 'text';
  value?: number | string;
};

const StyledLabel = styled.label({
  display: 'block',
  margin: '0px 0px 10px',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: fontSizes.medium,
  color: colors.text.primary,
  lineHeight: '20px',
  fontFamily: 'soleil',
  fontWeight: '600',
  width: 'initial',
});

const Container = styled.div({
  position: 'relative',
});

const StyledNumberFormat = styled(NumberFormat)`
  border: solid 1px ${colors.borderAction};
  border-radius: 0;
  font-size: 1.4rem;
  height: 44px;
  padding: 5px 15px 5px 27px;
  width: 100%;
  text-align: right;
  &:hover {
    background: #fff;
  }
  &::placeholder {
    color: #ccc;
  }
  :focus {
    outline: none;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: none;
  }
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

export const PriceInput = forwardRef<NumberFormat, PriceInputProps>(
  (
    {
      allowNegative = false,
      currencySymbol = '$',
      dataTestId,
      decimalScale = 2,
      decimalSeparator = '.',
      defaultValue,
      fixedDecimalScale = true,
      label,
      onValueChange,
      prefix = '',
      suffix = '',
      thousandSeparator = ',',
      ...rest
    }: PriceInputProps,
    ref
  ) => {
    const handleValueChange = (values: {
      formattedValue: string;
      value: string;
    }) => {
      // Convert whole numbers to cents by multiplying by 100
      if (values.value) {
        const numericValue = parseFloat(values.value);
        if (!isNaN(numericValue)) {
          const centsValue = Math.round(numericValue * 100).toString();
          onValueChange(centsValue);
          return;
        }
      }
      onValueChange(values.value);
    };

    // Convert defaultValue from cents to dollars for display
    const displayValue = defaultValue
      ? (parseFloat(defaultValue as string) / 100).toString()
      : undefined;

    return (
      <>
        {label ? <StyledLabel htmlFor={label}>{label}</StyledLabel> : null}
        <Container>
          <StyledNumberFormat
            allowNegative={allowNegative}
            data-testid={dataTestId}
            decimalScale={decimalScale}
            decimalSeparator={decimalSeparator}
            fixedDecimalScale={fixedDecimalScale}
            id={label}
            isNumericString={false}
            onValueChange={handleValueChange}
            prefix={prefix || currencySymbol}
            ref={ref}
            suffix={suffix}
            thousandSeparator={thousandSeparator}
            value={displayValue}
            {...rest}
          />
        </Container>
      </>
    );
  }
);

PriceInput.displayName = 'PriceInput';
